import React from "react";
import { Link } from "react-router-dom";
import { OPPORTUNITIES } from "../data/mockData";
import OpportunityCard from "../components/OpportunityCard";
import { SBLogo } from "../components/WebLayout";
import SampleDataBanner from "../components/SampleDataBanner";
import {
  LayoutGrid, Store, Bookmark, ShoppingBag, MessagesSquare, Trophy, BarChart3, Settings,
  Bell, Plus, ArrowRight, MapPin, TrendingUp,
} from "lucide-react";

const NAV = [
  { icon: LayoutGrid, label: "Overview", active: true },
  { icon: Store, label: "Marketplace" },
  { icon: Bookmark, label: "Saved Territories" },
  { icon: ShoppingBag, label: "Purchases" },
  { icon: MessagesSquare, label: "Introductions" },
  { icon: Trophy, label: "Won Locations" },
  { icon: BarChart3, label: "Reports" },
  { icon: Settings, label: "Settings" },
];

const STATS = [
  { label: "Available Opportunities", value: "42", tone: "gold" },
  { label: "Saved Territories", value: "3" },
  { label: "Purchased Opportunities", value: "8" },
  { label: "Active Introductions", value: "5" },
  { label: "Won Locations", value: "2" },
];

const PURCHASED = [
  { id: "opp-005", label: "Warehouse — Romeoville, IL", status: "Introduction Sent" },
  { id: "opp-002", label: "Manufacturing — Bolingbrook, IL", status: "Meeting Scheduled" },
  { id: "opp-003", label: "Office — Naperville, IL", status: "Contacted" },
];

const TERRITORIES = [
  { name: "Chicago Metro — Warehouses", demand: 82 },
  { name: "Dallas — Apartments", demand: 64 },
  { name: "Houston — Logistics", demand: 91 },
];

const ALERTS = [
  { text: "New opportunity in Chicago (Warehouses, 165+ employees)", time: "12m ago" },
  { text: "Price drop on Naperville Office listing ($399 → $349)", time: "2h ago" },
  { text: "Territory match: 3 new listings in Dallas", time: "Yesterday" },
];

export default function OperatorDashboard() {
  return (
    <div className="min-h-screen bg-[#080B0D] text-white flex">
      <aside className="hidden lg:flex flex-col w-64 border-r border-[#2A2F34] bg-[#0B0E11] p-5 sticky top-0 h-screen">
        <Link to="/"><SBLogo size={18} /></Link>
        <div className="mt-8 sb-eyebrow">Operator</div>
        <nav className="mt-3 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button key={n.label} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition-colors ${n.active ? "bg-[#FFC107]/10 text-[#FFC107]" : "text-[#A1A1AA] hover:text-white hover:bg-white/5"}`}>
                <Icon size={16} /> {n.label}
              </button>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 backdrop-blur-md bg-[#080B0D]/85 border-b border-[#2A2F34]">
          <div className="px-6 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="sb-eyebrow">Overview</div>
              <h1 className="text-[22px] font-black mt-1">Welcome back, Meridian Vending Co.</h1>
              <div className="text-[12px] text-[#A1A1AA]">Wednesday, Feb 26, 2026</div>
            </div>
            <div className="flex items-center gap-3">
              <button className="w-9 h-9 rounded-full bg-[#15191D] border border-[#2A2F34] flex items-center justify-center relative">
                <Bell size={15} /><span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[#FFC107]" />
              </button>
              <button className="sb-btn-primary text-[11px]" style={{ padding: "10px 14px" }}>
                <Plus size={14} /> Create Territory
              </button>
            </div>
          </div>
        </header>

        <div className="p-6 space-y-8">
          <SampleDataBanner />
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
            {STATS.map((s) => (
              <div key={s.label} className="sb-card p-5">
                <div className="sb-eyebrow" style={{ fontSize: 10 }}>{s.label}</div>
                <div className={`mt-3 text-[28px] font-black ${s.tone === "gold" ? "text-[#FFC107]" : "text-white"}`}>{s.value}</div>
              </div>
            ))}
          </div>

          <section>
            <div className="flex items-center justify-between">
              <div><div className="sb-eyebrow">New Matches</div><h2 className="mt-1 text-[22px] font-black">Curated for your territory</h2></div>
              <Link to="/marketplace" className="text-[12px] text-[#A1A1AA] hover:text-[#FFC107] flex items-center gap-1">View all <ArrowRight size={12} /></Link>
            </div>
            <div className="mt-4 grid md:grid-cols-2 xl:grid-cols-4 gap-4">
              {OPPORTUNITIES.slice(0, 4).map((o) => <OpportunityCard key={o.id} opp={o} compact />)}
            </div>
          </section>

          <section className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
            <div className="sb-card p-6">
              <div className="sb-eyebrow">Recently Purchased</div>
              <div className="mt-4 divide-y divide-[#2A2F34]">
                {PURCHASED.map((p) => (
                  <Link to={`/marketplace/${p.id}?state=unlocked`} key={p.id} className="py-4 flex items-center justify-between hover:bg-white/[.02] px-2 rounded-md">
                    <div className="flex items-center gap-3">
                      <MapPin size={14} className="text-[#FFC107]" />
                      <div>
                        <div className="text-[14px] font-bold">{p.label}</div>
                        <div className="text-[11px] text-[#A1A1AA]">Opp ID: {p.id}</div>
                      </div>
                    </div>
                    <span className="sb-pill sb-pill-gold" style={{ fontSize: 10 }}>{p.status}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="sb-card p-6">
              <div className="sb-eyebrow">Saved Territory Demand</div>
              <div className="mt-4 space-y-4">
                {TERRITORIES.map((t) => (
                  <div key={t.name}>
                    <div className="flex items-center justify-between text-[13px]">
                      <span>{t.name}</span>
                      <span className="text-[#FFC107] font-bold">{t.demand}%</span>
                    </div>
                    <div className="mt-2 sb-progress-track">
                      <div className="sb-progress-fill" style={{ width: `${t.demand}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section>
            <div className="sb-eyebrow flex items-center gap-2"><TrendingUp size={12} className="text-[#FFC107]" /> Opportunity Alerts</div>
            <div className="mt-4 sb-card divide-y divide-[#2A2F34]">
              {ALERTS.map((a, i) => (
                <div key={i} className="p-4 flex items-center justify-between">
                  <div className="text-[13px]">{a.text}</div>
                  <div className="text-[11px] text-[#52525b]">{a.time}</div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
